import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Check, Copy, MapPin, X } from 'lucide-react';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MRS_CHIP_SHAPE } from '@/components/MrsLink';
import { cn, isClamped } from '@/lib/utils';
import type { BinInfoDto } from '../../types';
import { qty } from '../../utils/picklistProgress';
import { unitWord, type PostRow, type RowFigures } from './postRows';

interface MrsLineDetailsPopoverProps {
  /** The row whose details are open, and the item cell it opened from; null when closed. */
  target: { row: PostRow; anchor: HTMLElement } | null;
  /** The row's item group's DISPLAY bins (only the row's own picked units are read from them). */
  bins: readonly BinInfoDto[];
  figures: RowFigures | null;
  onClose: () => void;
}

/**
 * PT-5 deliverable 7 — the MRS line details popover: ONE instance per dialog, anchored to the item cell that was
 * clicked (no Radix root per row), rendered inside the Post dialog's tree so it is a nested layer. Radix's
 * collision handling picks the side with more room; its height is capped to what is available and it scrolls.
 *
 * It shows picked, posting and posted units for THIS line only — never how allocation is split across MRS lines.
 * The description is the MRS line's own (O-33: a line whose description differs from the item's is read here).
 */
export function MrsLineDetailsPopover({ target, bins, figures, onClose }: MrsLineDetailsPopoverProps) {
  // A virtual anchor: the clicked item cell. Memoised on the target, never written during render.
  const anchorRef = useMemo(() => ({ current: target?.anchor ?? null }), [target]) as RefObject<HTMLElement>;

  return (
    <Popover open={target !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <PopoverAnchor virtualRef={anchorRef} />
      <PopoverContent
        role="dialog"
        aria-label="MRS line details"
        align="start"
        collisionPadding={8}
        // Focus the popover itself on open, not its first button: the Close (✕) button's tooltip opens on focus, so
        // it used to pop up unasked and the FIRST Escape only dismissed that tooltip (e2e 19, 2026-10-07). Tab still
        // reaches Close; one Escape now closes the popover and leaves the Post dialog open.
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
        className="z-[70] max-h-[var(--radix-popover-content-available-height)] w-[380px] max-w-[calc(100vw-16px)] overflow-y-auto rounded-xl bg-card p-3.5 text-foreground"
      >
        {target && figures && (
          <PopoverBody key={target.row.id} row={target.row} bins={bins} figures={figures} onClose={onClose} />
        )}
      </PopoverContent>
    </Popover>
  );
}

function PopoverBody({ row, bins, figures, onClose }: { row: PostRow; bins: readonly BinInfoDto[]; figures: RowFigures; onClose: () => void }) {
  const nameRef = useRef<HTMLParagraphElement | null>(null);
  const [nameLong, setNameLong] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // "See more" is decided by a real overflow measurement (the ticket's rule), not a character count.
  useLayoutEffect(() => {
    if (nameRef.current && !nameOpen) setNameLong(isClamped(nameRef.current));
  }, [row.itemName, nameOpen]);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1600);
    return () => clearTimeout(timer);
  }, [copied]);

  const copyRemarks = async () => {
    try {
      await navigator.clipboard.writeText(row.remarks);
      setCopied(true);
    } catch {
      // A failed clipboard write shows no "Copied" (the ticket's rule; the prototype showed it anyway).
    }
  };

  const u = (n: number) => `${qty(n)} ${unitWord(row.unit, n)}`;
  const facts = [
    { k: 'Requested', v: u(figures.requested) },
    { k: 'Already issued', v: u(figures.released) },
    { k: 'Picked for this MRS', v: u(row.picked) },
    { k: 'Ready to post', v: u(row.max) },
  ];

  // This line's own picked units per display bin (a serial display bin is one slot; a merged one lists each line).
  const binRows = bins.flatMap((bin) => {
    const line = (bin.lines ?? []).find((l) => l.picklistItemId === row.id);
    if (!line || line.currentlyPickedQuantity <= 0) return [];
    const isSerialSlot = bin.inventoryNumberId != null;
    const state = line.postedQuantity >= line.currentlyPickedQuantity
      ? 'Posted'
      : isSerialSlot && (bin.postingQuantity ?? 0) > 0
        ? 'Posting'
        : 'Picked';
    return [{ key: line.picklistItemBinId, bin: bin.binNumber, serial: bin.inventoryNumber, qty: u(line.currentlyPickedQuantity), state }];
  });

  return (
    <>
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">MRS line details</div>
          <div className="mt-[3px] break-words font-mono text-[13.5px] font-bold">{row.itemCode}</div>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="inline-flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="h-[15px] w-[15px]" aria-hidden />
            </button>
          </TooltipTrigger>
          <TooltipContent>Close</TooltipContent>
        </Tooltip>
      </div>

      <p
        ref={nameRef}
        className={cn(
          'mt-1.5 select-text text-xs leading-normal text-muted-foreground',
          nameOpen ? 'max-h-[108px] overflow-y-auto pr-1' : 'line-clamp-4',
        )}
      >
        {row.itemName}
      </p>
      {(nameLong || nameOpen) && (
        <button
          type="button"
          aria-expanded={nameOpen}
          onClick={() => setNameOpen((o) => !o)}
          className="mt-1 text-[11.5px] font-semibold text-info hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {nameOpen ? 'See less' : 'See more'}
        </button>
      )}

      <div className="mt-3 grid grid-cols-[max-content_minmax(0,1fr)] items-baseline gap-x-3.5 gap-y-[7px] border-t border-border pt-3 text-xs">
        <span className="text-muted-foreground">MRS</span>
        <span><span className={cn(MRS_CHIP_SHAPE, 'text-info')}>{row.mrsNumber}</span></span>
        <span className="text-muted-foreground">Requestor</span>
        <span className="font-medium">{row.requestorFullName || '—'}</span>
        {/* O-10: department and cost centre are one value, shown once. */}
        <span className="text-muted-foreground">Cost center</span>
        <span>{row.departmentName || '—'}</span>
        <span className="text-muted-foreground">End use</span>
        <span>{row.endUseName || '—'}</span>
        <span className="text-muted-foreground">Remarks</span>
        <span className="min-w-0">
          {row.remarks ? (
            <button
              type="button"
              onClick={() => void copyRemarks()}
              aria-label={`Copy remarks: ${row.remarks}`}
              title={copied ? 'Copied' : 'Click to copy'}
              className={cn(
                '-ml-[7px] -mt-0.5 inline-flex max-w-full cursor-copy items-start gap-1.5 rounded-md border px-[7px] py-0.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                copied ? 'border-success/40 bg-success/[0.06]' : 'border-transparent hover:border-input',
              )}
            >
              <span className="break-words">{row.remarks}</span>
              {copied ? (
                <Check className="mt-0.5 h-3 w-3 shrink-0 text-success" aria-hidden />
              ) : (
                <Copy className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
              )}
              {copied && <span className="shrink-0 whitespace-nowrap text-[10.5px] font-semibold text-success">Copied</span>}
            </button>
          ) : (
            '—'
          )}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-2 rounded-[10px] border border-border">
        {facts.map((f) => (
          <div key={f.k} className="min-w-0 border-b border-r border-border/60 px-2.5 py-2">
            <div className="truncate text-[10.5px] text-muted-foreground">{f.k}</div>
            <div className="mt-0.5 text-[13.5px] font-semibold tabular-nums">{f.v}</div>
          </div>
        ))}
      </div>

      {binRows.length > 0 && (
        <div className="mt-3">
          <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.04em] text-muted-foreground">
            {row.isSerial ? 'Serials picked' : 'Picked from'}
          </div>
          <div className="flex flex-col gap-1">
            {binRows.map((b) => (
              <div key={b.key} className="flex items-center gap-2 rounded-[7px] bg-muted/45 px-2 py-[5px] text-xs">
                <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden />
                <span className="font-mono text-[11.5px] font-semibold">{b.bin}</span>
                {b.serial && <span className="font-mono text-[11.5px] font-medium text-muted-foreground">{b.serial}</span>}
                <span className="ml-auto tabular-nums">{b.qty}</span>
                <span className="text-[10.5px] text-muted-foreground">{b.state}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
